<template>
  <div>
    <LoadingSpinner v-if="!isReviewStep" :show="UIStore.is_loading" size="large" smallText />
    <SignInvestmentLoader
      v-if="showSigningLoader"
      :animationData="UIStore.is_dark_mode ? darkModeAnimation : lightModeAnimation"
    />
    <ConfirmChangeInvestmentModal
      :open="purchaseFlowStore.is_change_investment_modal_open"
      @close-modal="purchaseFlowStore.is_change_investment_modal_open = false"
      @confirm-changes="handleConfirmChanges"
      @discard-changes="handleDiscardChanges"
    />
    <JumpToReviewModal
      :open="purchaseFlowStore.is_fast_track_modal_open"
      @close-modal="purchaseFlowStore.is_fast_track_modal_open = false"
      @continue-investment="handleJumpToBanking"
      @next-step="handleContinue"
    />
    <DisclaimerModal
      :open="purchaseFlowStore.is_show_disclaimer_modal"
      @close-modal="purchaseFlowStore.is_show_disclaimer_modal = false"
      @confirm="router.push('/dashboard')"
      disclaimer="You're about to leave the investment process. Your progress has been saved, so you can pick up right where you left off whenever you're ready to continue."
      title="Leave Investment?"
      subheader="Are you sure?"
      cta_text="Return to Dashboard"
    />
    <div v-if="purchaseFlowStore.investment_steps && experimentReady" class="flex flex-col h-full">
      <div>
        <div class="py-[0.875rem]">
          <!-- V2 offering screen: product=generic + user is in test group -->
          <OfferingsScreenV2
            v-if="purchaseFlowStore.current_step === 0 && experiment.showV2OfferingScreen.value"
          />
          <!-- V1 offering screen: default path -->
          <OfferingsScreenV3 v-else-if="purchaseFlowStore.current_step === 0">
            <template v-slot:header>
              <div class="mb-6 md:mb-[3rem]">
                <h3
                  class="block text-sm md:text-2xl font-medium tracking-[-0.48] text-left text-strong"
                >
                  Invest In Phoenix Energy
                </h3>
                <h1
                  class="text-3xl md:text-4xl mt-[.25rem] font-medium tracking-[-0.96] text-strongest"
                >
                  Explore our Bond Offerings
                </h1>
              </div>
            </template>
          </OfferingsScreenV3>
        </div>
        <div
          v-if="purchaseFlowStore.current_step > 0"
          class="pb-6"
          :class="areChangeMetricsHidden ? 'pb-[76px]' : 'pb-[135px]'"
        >
          <PurchaseFlowControlCenter @continue="handleContinue" @back="handleBack" />
          <component
            ref="currentStepRef"
            class="w-full mt-[1.375rem] lg:mt-10"
            :is="
              getStepComponent(
                purchaseFlowStore.investment_steps[purchaseFlowStore.current_step - 1]?.name
              )
            "
            :key="purchaseFlowStore.current_step"
            @substep-change="handleSubstepChange"
            @continue="handleContinue"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, watch, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  usePurchaseFlowStore,
  useUIStore,
  useDataStore,
  useCrudStore,
  useSchemaStore,
} from "@/stores";
import { LoadingSpinner, DisclaimerModal } from "@/components";
import { isRetirementAccountType } from "@/utilities";
import SignInvestmentLoader from "@/components/SignInvestmentLoader.vue";
import darkModeAnimation from "@/assets/animations/dark-mode.json";
import lightModeAnimation from "@/assets/animations/light-mode.json";

import OfferingsScreen from "./OfferingsScreen.vue";
import OfferingsScreenV2 from "./OfferingsScreenV2.vue";
import OfferingsScreenV3 from "./OfferingsScreenV3.vue";
import { useOfferingExperiment } from "@/composables/useOfferingExperiment";
import { useStepOrderExperiment } from "@/composables/useStepOrderExperiment";
import { useAnalytics } from "@/composables/useAnalytics";
import PF_SelectPaymentFrequency from "./PF_SelectPaymentFrequency.vue";
import PF_SelectAmount from "./PF_SelectAmount.vue";
import PF_TermAndYield from "./PF_TermAndYield.vue";
import PF_SelectInvestmentType from "./PF_SelectInvestmentType.vue";
import PF_PersonalInfo from "./PF_PersonalInfo.vue";
import PF_SuitabilityInfo from "./PF_SuitabilityInfo.vue";
import PF_JointInfo from "./PF_JointInfo.vue";
import PF_EntityInfo from "./PF_EntityInfo.vue";
import PF_Accreditation from "./PF_Accreditation.vue";
import PF_FundingDistribution from "./PF_FundingDistribution.vue";
import PF_IRADetails from "./PF_IRADetails.vue";
import PF_FinalizeInvestment from "./PF_FinalizeInvestment.vue";
import PF_ReviewAndSign from "./PF_ReviewAndSign.vue";
import PurchaseFlowControlCenter from "./PurchaseFlowUtilities/PurchaseFlowControlCenter.vue";
import ConfirmChangeInvestmentModal from "./PurchaseFlowUtilities/ConfirmChangeInvestmentModal.vue";
import JumpToReviewModal from "./PurchaseFlowUtilities/JumpToReviewModal.vue";

const purchaseFlowStore = usePurchaseFlowStore();
const UIStore = useUIStore();
const dataStore = useDataStore();
const crudStore = useCrudStore();
const schemaStore = useSchemaStore();
const route = useRoute();
const router = useRouter();

const currentStepRef = ref(null);
const showSigningLoader = ref(false);
const experimentReady = ref(false);

const experiment = useOfferingExperiment();
const stepOrderExperiment = useStepOrderExperiment();
const {
  trackOfferingPageViewed,
  trackFlowStepEntered,
  trackExperimentAssigned,
  updateExperimentContext,
  investmentTypeSelected,
  paymentMethodSelected,
  reinvestStarted,
} = useAnalytics();

const stepComponents = {
  "Payment Frequency": PF_SelectPaymentFrequency,
  Amount: PF_SelectAmount,
  "Term and Yield": PF_TermAndYield,
  "Investment Type": PF_SelectInvestmentType,
  "Personal Information": PF_PersonalInfo,
  "Suitability Questionnaire": PF_SuitabilityInfo,
  "Joint Information": PF_JointInfo,
  "Entity Information": PF_EntityInfo,
  Accreditation: PF_Accreditation,
  "Funding and Distribution": PF_FundingDistribution,
  "IRA Details": PF_IRADetails,
  "Finalize Investment": PF_FinalizeInvestment,
  "Review and Sign": PF_ReviewAndSign,
};

onMounted(async () => {
  const { rolloverInvestment, finishPendingInvestment } = route.query;
  await Promise.all([
    experiment.initExperiment(),
    stepOrderExperiment.initExperiment(),
    purchaseFlowStore.initializePurchaseStore(),
  ]);
  purchaseFlowStore.setInvestmentStepsForType(stepOrderExperiment.isInTestGroup.value);

  if (experiment.productParam.value) {
    trackExperimentAssigned({
      experimentKey: "sign-up-ctr",
      variant: experiment.isInTestGroup.value ? "test" : "control",
      productParameter: experiment.productParam.value,
      entryUrl: window.location.href,
    });
  }

  // Rollover and finish-pending flows take priority over product-param routing.
  // finishPendingInvestment only ever arrives from the dashboard's explicit
  // "Finish your pending investment" CTA — nothing resumes the flow automatically.
  if (finishPendingInvestment) {
    await handleContinueInvestment();
    experimentReady.value = true;
    return;
  }

  if (rolloverInvestment) {
    await handleJumpToRollover(rolloverInvestment);
    // Reinvestments now start on the offering selection screen like any other flow.
    if (purchaseFlowStore.current_step === 0) {
      trackOfferingPageViewed();
    }
    experimentReady.value = true;
    return;
  }

  if (experiment.skipToAmountWithOffering.value && experiment.preselectedOfferingType.value) {
    await purchaseFlowStore.applyProductParam(experiment.preselectedOfferingType.value);
  }
  if (purchaseFlowStore.current_step === 0) {
    trackOfferingPageViewed();
  }
  experimentReady.value = true;
});

onUnmounted(() => {
  purchaseFlowStore.current_step = 0;
  purchaseFlowStore.current_substep = 1;
  purchaseFlowStore.selected_rollover = null;
  purchaseFlowStore.has_fast_track_been_shown = false;
  schemaStore.show_input_errors = false;
});

watch(
  () => purchaseFlowStore.current_step,
  (newStep, oldStep) => {
    if (newStep) {
      window.scrollTo(0, 0);
    }
    // Take investment snap so we know when metrics are changing (amount, offering, is_compound)
    if (newStep >= 3 && oldStep < 3) {
      purchaseFlowStore.takeInvestmentSnapshot();
    }
    if (newStep > 0) {
      const stepName = purchaseFlowStore.investment_steps?.[newStep - 1]?.name;
      if (stepName) {
        trackFlowStepEntered(stepName.toLowerCase().replace(/\s+/g, "_"));
      }

      const inv = purchaseFlowStore.current_investment;
      const offering = purchaseFlowStore.selected_offering;
      const ctx = {};
      if (offering?.type) ctx.offering_type = offering.type;
      if (inv?.amount) ctx.amount = inv.amount;
      if (inv?.is_compounded != null)
        ctx.distribution_method = inv.is_compounded ? "compounded" : "monthly";
      if (inv?.type) ctx.investment_type = inv.type;
      if (Object.keys(ctx).length) updateExperimentContext(ctx);

      if (oldStep > 0) {
        const prevStepName = purchaseFlowStore.investment_steps?.[oldStep - 1]?.name;
        if (prevStepName === "Investment Type" && inv?.type) investmentTypeSelected(inv.type);
        if (prevStepName === "Funding and Distribution" && inv?.payment_method)
          paymentMethodSelected(inv.payment_method);
      }
    }
  }
);

watch(
  () => [
    purchaseFlowStore.current_step,
    purchaseFlowStore.current_substep,
    purchaseFlowStore.investment_steps,
  ],
  () => {
    window.scrollTo(0, 0);
    purchaseFlowStore.handleChangeURL(router, route);
  },
  { immediate: true }
);

watch(
  () => [
    purchaseFlowStore.current_investment.amount,
    purchaseFlowStore.current_investment.is_compounded,
    purchaseFlowStore.selected_offering,
  ],
  () => {
    if (purchaseFlowStore.current_step > 2) {
      if (!purchaseFlowStore.investment_snapshot) {
        purchaseFlowStore.takeInvestmentSnapshot();
      } else {
        // Only detect changes if user has already confirmed these steps before (not a first-time pass)
        const hasCompletedBefore =
          (purchaseFlowStore.current_investment.last_completed_step || 0) >= 3;
        const hasChanged = hasCompletedBefore && purchaseFlowStore.getInvestmentChanges() !== null;
        purchaseFlowStore.has_investment_changed = hasChanged;
      }
    }
  }
);

watch(
  () => purchaseFlowStore.regDTier,
  (newTier, oldTier) => {
    if (newTier !== oldTier && purchaseFlowStore.selected_offering?.type === "Reg D") {
      const dataStore = useDataStore();
      const matchingOfferings = dataStore.offerings.filter(
        (o) =>
          o.type === "Reg D" &&
          o.tier === newTier &&
          o.is_short_term === (purchaseFlowStore.selected_offering.is_short_term || false)
      );

      if (matchingOfferings.length) {
        const best = matchingOfferings.reduce((a, b) =>
          b.interest_rate > a.interest_rate ? b : a
        );
        purchaseFlowStore.selected_offering = best;
        purchaseFlowStore.current_investment.offering_id = best.record_id;
      }
    }
  }
);

const isReviewStep = computed(() => {
  if (!purchaseFlowStore.current_step || !purchaseFlowStore.investment_steps) return false;
  return (
    purchaseFlowStore.current_step > 0 &&
    purchaseFlowStore.investment_steps[purchaseFlowStore.current_step - 1]?.name ===
      "Review and Sign"
  );
});

const areChangeMetricsHidden = computed(() => {
  return (
    purchaseFlowStore.current_step < 3 ||
    purchaseFlowStore.currentStep?.name === "Finalize Investment"
  );
});

const getStepComponent = (stepName) => {
  return stepComponents[stepName] || null;
};

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

async function handleContinue() {
  const step = currentStepRef.value;
  const currentSubstep = purchaseFlowStore.current_substep;

  // 1. Block continuing past the Amount step if the investment (amount/offering)
  // was since edited below the required minimum via the change-investment controls.
  if (purchaseFlowStore.current_step > 2 && purchaseFlowStore.isCurrentInvestmentBelowMinimum) {
    UIStore.animateNotificationAlert({
      type: "error",
      icon: "InfoWarning",
      message: `Minimum investment for this offering is ${formatCurrency(purchaseFlowStore.investmentMinimumAmount)}.`,
    });
    return;
  }

  // 2. Run step-specific validation
  if (step?.validate) {
    const result = await step.validate();

    // console.log("Validation result for step", purchaseFlowStore.currentStep.name, ":", result);
    if (!result.proceed) {
      dataStore.logInvestorActivity({ type: "validation_failure", validationResult: result });
      return;
    } else {
      dataStore.logInvestorActivity({ type: "step_completed" });
    }
  }

  try {
    if (step?.beforeContinue) {
      const result = await step.beforeContinue(currentSubstep);
      if (!result.proceed) {
        return;
      }
    }

    // 2. Check for investment changes (once the purchase flow control center allows metric changes mid-flow)
    if (purchaseFlowStore.has_investment_changed && purchaseFlowStore.current_step > 2) {
      purchaseFlowStore.is_change_investment_modal_open = true;
      return;
    }
    purchaseFlowStore.updateLastCompletedStep();
    // 3. Proceed with navigation immediately
    proceedToNextStep();

    // 4. Fire off save in background
    if (step?.afterContinue) {
      step.afterContinue(currentSubstep).catch((error) => {
        console.error("Background save failed:", error);
      });
    }
  } catch (error) {
    console.error("Step error:", error);
  }
}

// TODO: Make sure this is attached to invetor activity as well
async function handleSubstepChange(newSubstep) {
  const step = currentStepRef.value;
  const currentSubstep = purchaseFlowStore.current_substep;

  // Same tab - do nothing
  if (newSubstep === currentSubstep) return;

  // Going backward - always allowed
  if (newSubstep < currentSubstep) {
    purchaseFlowStore.current_substep = newSubstep;
    return;
  }

  // Going forward - validate all substeps in between
  if (step?.validateRange) {
    const result = await step.validateRange(currentSubstep - 1, newSubstep - 1);

    if (!result.proceed) {
      // console.log(
      //   `Validation failed at "${result.tabName}":`,
      //   result.error_messages || result.invalid_fields
      // );

      purchaseFlowStore.current_substep = result.failedAt + 1;

      UIStore.animateNotificationAlert({
        type: "error",
        icon: "Info",
        message: `Please complete "${result.tabName}" first.`,
      });
      return;
    }
  }

  if (step?.beforeContinue) {
    const result = await step.beforeContinue(currentSubstep);
    if (!result.proceed) {
      return;
    }
  }
  purchaseFlowStore.updateLastCompletedStep();
  purchaseFlowStore.current_substep = newSubstep;

  // console.log(`Navigated to substep ${newSubstep} from ${currentSubstep}.`);

  // All validations passed - save
  if (step?.afterContinue) {
    step.afterContinue(currentSubstep).catch((error) => {
      console.error("Background save failed:", error);
    });
  }
}

async function proceedToNextStep() {
  const hasSubsteps = purchaseFlowStore.currentStep?.sub_steps?.length > 0;
  const totalSubsteps = purchaseFlowStore.currentStep?.sub_steps?.length || 0;

  if (hasSubsteps && purchaseFlowStore.current_substep < totalSubsteps) {
    purchaseFlowStore.current_substep += 1;
  } else if (purchaseFlowStore.current_step < purchaseFlowStore.investment_steps.length) {
    purchaseFlowStore.current_step += 1;
    purchaseFlowStore.current_substep = 1;
  } else {
    await handleCompleteInvestment();
  }
}

// TODO: Do we want to track going backwards in investor activity?
function handleBack() {
  const hasSubsteps = purchaseFlowStore.currentStep?.sub_steps?.length > 0;

  if (hasSubsteps && purchaseFlowStore.current_substep > 1) {
    purchaseFlowStore.current_substep -= 1;
  } else if (purchaseFlowStore.current_step >= 1) {
    purchaseFlowStore.current_step -= 1;
    const prevStepData = purchaseFlowStore.investment_steps[purchaseFlowStore.current_step - 1];
    purchaseFlowStore.current_substep = prevStepData?.sub_steps?.length || 1;
  }
}

// For Changing Investment Metrics Mid-Flow (amount, offering, is_compound)
async function handleConfirmChanges() {
  purchaseFlowStore.has_investment_changed = false;
  const changes = purchaseFlowStore.getInvestmentChanges();

  dataStore.logInvestorActivity({
    type: "investment_changed",
    details: changes,
  });
  purchaseFlowStore.takeInvestmentSnapshot();
  await purchaseFlowStore.saveInvestmentProgress();
  if (!isReviewStep.value) {
    proceedToNextStep();
  }
}

function handleDiscardChanges() {
  if (!purchaseFlowStore.investment_snapshot) return;

  purchaseFlowStore.current_investment.amount = purchaseFlowStore.investment_snapshot.amount;
  purchaseFlowStore.current_investment.is_compounded =
    purchaseFlowStore.investment_snapshot.is_compounded;
  // Restore alongside the offering — leaving a stale short-term flag on a restored
  // long-term offering produces an offering/term combination with no signing template.
  purchaseFlowStore.current_investment.is_short_term =
    purchaseFlowStore.investment_snapshot.is_short_term;
  purchaseFlowStore.selected_offering = purchaseFlowStore.investment_snapshot.offering;

  purchaseFlowStore.has_investment_changed = false;
  purchaseFlowStore.takeInvestmentSnapshot();
}

// For final step when user clicks "Complete Investment"
async function handleCompleteInvestment() {
  let log = {
    step: "Review and Complete Investment",
    substep: "",
    status: "Awaiting completion",
    message: `User clicked Complete Investment for Investment ID: ${purchaseFlowStore.current_investment.record_id}`,
    url: route.fullPath,
    investment_id: purchaseFlowStore.current_investment.record_id,
  };
  log = dataStore.setDefaultFields(log, "investoractivity");
  crudStore.insert("investoractivity", [log]);

  const isIRA = isRetirementAccountType(purchaseFlowStore.current_investment?.type);
  const isS1QuietPeriod =
    dataStore.s1_offerings_in_quiet_period && purchaseFlowStore.selected_offering?.type === "S1";

  // During S1 quiet period, all investments (including IRA) just get pending status
  if (isS1QuietPeriod) {
    UIStore.is_loading = true;
    await purchaseFlowStore.setPendingS1Status();
    await new Promise((resolve) => setTimeout(resolve, 3000));
    UIStore.is_loading = false;
    router.push(`/thank-you?investment_id=${purchaseFlowStore.current_investment.record_id}`);
    return;
  }

  // IRA outside quiet period — set signature date, skip signing link
  if (isIRA) {
    UIStore.is_loading = true;
    await purchaseFlowStore.setSignatureDateForIRA();
    await new Promise((resolve) => setTimeout(resolve, 3000));
    UIStore.is_loading = false;
    router.push(`/thank-you?investment_id=${purchaseFlowStore.current_investment.record_id}`);
    return;
  }

  // Standard flow — generate signing link and redirect
  showSigningLoader.value = true;
  const loaderStart = Date.now();

  if (purchaseFlowStore.signing_link_invalidated || !purchaseFlowStore.signing_link) {
    const result = await purchaseFlowStore.generateSigningLink(
      purchaseFlowStore.current_investment.record_id
    );
    if (!result) {
      showSigningLoader.value = false;
      return;
    }
  }

  const remaining = Math.max(0, 11000 - (Date.now() - loaderStart));
  if (remaining > 0) {
    await new Promise((resolve) => setTimeout(resolve, remaining));
  }

  window.location.href = purchaseFlowStore.signing_link;
  showSigningLoader.value = false;
}

// Resume path for the dashboard's "Finish your pending investment" CTA. This is an
// explicit, user-initiated jump — the flow no longer offers to resume on its own.
async function handleContinueInvestment() {
  const investment = purchaseFlowStore.current_investment;

  // Ensure offering is resolved before jumping ahead
  if (!purchaseFlowStore.selected_offering) {
    purchaseFlowStore.selected_offering = purchaseFlowStore.resolveOffering(investment.offering_id);
  }

  const steps = purchaseFlowStore.investment_steps;
  const lastStep = investment.last_completed_step || 1;
  const lastSubstep = investment.last_completed_substep || 1;

  const stepData = steps?.[lastStep - 1];
  const totalSubsteps = stepData?.sub_steps?.length || 0;
  const totalSteps = steps?.length || 0;

  if (totalSubsteps && lastSubstep < totalSubsteps) {
    // More substeps remaining — go to the next substep
    purchaseFlowStore.current_step = lastStep;
    purchaseFlowStore.current_substep = lastSubstep + 1;
  } else if (lastStep < totalSteps) {
    // Move to next step, reset substep
    purchaseFlowStore.current_step = lastStep + 1;
    purchaseFlowStore.current_substep = 1;
  } else {
    // Already on the final step — stay put instead of jumping past it
    purchaseFlowStore.current_step = lastStep;
    purchaseFlowStore.current_substep = lastSubstep;
  }
  await purchaseFlowStore.saveInvestmentProgress();
}

function handleJumpToBanking() {
  //hi
  if (isRetirementAccountType(purchaseFlowStore.current_investment?.type)) {
    purchaseFlowStore.current_step =
      purchaseFlowStore.investment_steps.findIndex((step) => step.name === "IRA Details") + 1;
  } else {
    purchaseFlowStore.current_step =
      purchaseFlowStore.investment_steps.findIndex(
        (step) => step.name === "Funding and Distribution"
      ) + 1;
  }

  purchaseFlowStore.current_substep = 1;
  purchaseFlowStore.is_fast_track_modal_open = false;
}

async function handleJumpToRollover(investment_id) {
  // Land on the offering selection screen rather than jumping straight to Amount.
  // Reinvestors coming off a long-term S1 or Reg D used to inherit the parent's
  // offering, which forced them to navigate backwards to pick something else
  // (e.g. Flex Notes). The offering is now theirs to choose.
  purchaseFlowStore.current_step = 0;
  purchaseFlowStore.current_substep = 1;
  purchaseFlowStore.is_fast_track_modal_open = false;

  let investment = dataStore.investments.find((inv) => inv.record_id === investment_id);

  if (investment) {
    // Subtract any pending unsigned children from rollover_available
    const pendingAmount = (dataStore.investments || [])
      .filter(
        (inv) =>
          inv.status === "Pending" &&
          !inv.signature_date &&
          inv.parent_investment === investment.record_id
      )
      .reduce((sum, inv) => sum + (inv.amount || 0), 0);

    const adjustedRolloverAvailable = (investment.rollover_available || 0) - pendingAmount;

    purchaseFlowStore.selected_rollover = investment;
    reinvestStarted(investment_id, adjustedRolloverAvailable);
    // Pre-fill the whole available balance, floored to the $1,000 increment the Amount
    // step enforces. Flooring (not rounding) matters: the step caps the reinvestment at
    // the floored available amount, so a raw value like $12,480 would fail validation
    // with no way forward — blur rounds it up to $13,000, which is over the cap.
    purchaseFlowStore.current_investment.amount =
      Math.floor(adjustedRolloverAvailable / 1000) * 1000;
  }

  await purchaseFlowStore.saveInvestmentProgress();
}
</script>
